/* eslint-disable indent */
/* eslint-disable no-unused-vars */
/* eslint-disable quotes */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Table,
  Space,
  Button,
  Tooltip,
  message,
  Input,
  Modal,
  Form,
  Select,
  Tag,
  Row,
  Col,
  Card,
  Checkbox,
  DatePicker,
} from "antd";
import {
  EditOutlined,
  SearchOutlined,
  BarcodeOutlined,
  ScanOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  ClearOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import useCustomNavigate from "../../../components/customNavigationHook";
import "./statusUpdates.scss";
import API from "../../../api";
import urls from "../../../api/urls";
import constants from "../../../constants/constants";
import moment from "moment";
import { dateFormat_dmy, getUserAndPremiseId } from "../../../utils";
import search_icon from "../../../assests/images/search-icon.svg";

const { ROLES } = constants;
const { CENTRALOPERATOR } = ROLES;
const { RangePicker } = DatePicker;

const { GET_PRS_DATA_V2, DRS_UNDELIVERED_REASONS, DRS_ORDER_EXCEPTIONS } = urls;

// Function to get fresh initial state with non-mutated moment objects
const getInitialState = () => ({
  pagination: { current: 1, pageSize: 50, total: 0 },
  dateRange: [moment().startOf("day"), moment().endOf("day")],
  columnFilters: {
    awbNumber: "",
  },
  searchableFields: [],
});

// Function to initialize column filters based on data keys
const initializeColumnFilters = (dataSource, searchableFieldsArray = []) => {
  if (!dataSource || dataSource.length === 0) {
    // Return filters for searchable fields even if no data
    const filters = {};
    searchableFieldsArray.forEach((field) => {
      filters[field] = "";
    });
    return Object.keys(filters).length > 0 ? filters : { awbNumber: "" };
  }

  const sampleRecord = dataSource[0];
  const keys = Object.keys(sampleRecord);
  const filters = {};

  // Initialize filters only for searchable fields if provided
  if (searchableFieldsArray.length > 0) {
    searchableFieldsArray.forEach((field) => {
      if (keys.includes(field)) {
        filters[field] = "";
      }
    });
  } else {
    // Fallback to all keys if no searchable fields specified
    keys.forEach((key) => {
      filters[key] = "";
    });
  }

  return filters;
};

const StatusUpdates = ({ tabValue }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const navigate = useCustomNavigate();
  const initialState = getInitialState();
  const [pagination, setPagination] = useState(initialState.pagination);
  const [dateRange, setDateRange] = useState(initialState.dateRange);
  const [columnFilters, setColumnFilters] = useState(
    initialState.columnFilters
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // State to store searchable fields from API response
  const [searchableFields, setSearchableFields] = useState([]);

  // Debounce ref for search with timeout tracking
  const debounceRef = useRef({});

  // Flag to prevent multiple simultaneous API calls
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const apiCallInProgress = useRef(false);

  // Status update modal states
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [isBulkUpdate, setIsBulkUpdate] = useState(false);
  const [updateForm] = Form.useForm();
  const [selectedStatus, setSelectedStatus] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);

  // Reasons data
  const [undeliveredReasons, setUndeliveredReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState(null);

  // Scanner states
  const [currentAWB, setCurrentAWB] = useState("");
  const [scannerVisible, setScannerVisible] = useState(false);

  // Get user info from localStorage for role checking
  const userinfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
  const userRole = userinfo?.roles?.[0];

  // Check if user has Central Operator role
  const isCentralOperator = userRole === CENTRALOPERATOR;

  // Barcode scanner hook (simplified version)
  const useBarcodeScanner = (onScan, dependencies = []) => {
    const handleKeyDown = useCallback(
      (event) => {
        // If user is typing in an editable field, let the field handle it
        const target = event.target;
        const isEditableTarget =
          target &&
          (target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.isContentEditable);
        if (isEditableTarget) return;

        // Handle Enter key - complete barcode scan
        if (event.code === "Enter") {
          if (currentAWB.trim()) {
            onScan(currentAWB.trim());
            setCurrentAWB("");
          }
          return;
        }

        // Only append alphanumeric characters
        if (event.key.length === 1 && /^[a-zA-Z0-9]$/.test(event.key)) {
          setCurrentAWB((prev) => prev + event.key);
        }
      },
      [onScan, currentAWB, ...dependencies]
    );

    useEffect(() => {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);
  };

  // Handle AWB scan and auto-selection
  const handleAWBScan = useCallback(
    (awbNumber) => {
      const cleanAWB = awbNumber
        ?.toString()
        .replace(/[^a-zA-Z0-9]/g, "")
        .trim()
        .toUpperCase();

      if (!cleanAWB) {
        message.error("Invalid AWB number.");
        return;
      }

      // Check if AWB exists in the table data
      const matchingRecord = data.find(
        (record) => record.awbNumber?.toString().toUpperCase() === cleanAWB
      );

      if (!matchingRecord) {
        Modal.warning({
          title: "Shipment Not Found",
          content: `AWB ${cleanAWB} is not available in the current list.`,
          okText: "OK",
        });
        return;
      }

      // Check if already selected
      const isAlreadySelected = selectedRows.some(
        (r) => r.awbNumber === matchingRecord.awbNumber
      );

      if (isAlreadySelected) {
        message.info(`AWB ${cleanAWB} is already selected.`);
        return;
      }

      // Add to selected records
      setSelectedRowKeys((prev) => [...prev, matchingRecord.awbNumber]);
      setSelectedRows((prev) => [...prev, matchingRecord]);
      message.success(`AWB ${cleanAWB} selected successfully`);
    },
    [data, selectedRows]
  );

  // Initialize barcode scanner
  useBarcodeScanner(handleAWBScan, [data, selectedRows]);

  // Handle manual AWB input
  const handleManualAWBScan = () => {
    if (currentAWB.trim()) {
      handleAWBScan(currentAWB);
      setCurrentAWB("");
    }
  };

  useEffect(() => {
    if (isCentralOperator) {
      fetchAllData();
      fetchReasons();
    }
  }, [tabValue, isCentralOperator]);

  // Handle date range changes
  useEffect(() => {
    // Skip if this is the initial loading or if date range is from restoration
    if (isInitialLoading || apiCallInProgress.current) {
      return;
    }

    if (dateRange[0] && dateRange[1]) {
      const currentPage = pagination.current || 1;
      const currentPageSize = pagination.pageSize || 100;

      fetchAllData(currentPage, currentPageSize, columnFilters);
    }
  }, [dateRange, isInitialLoading]);

  // Cleanup debounce timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all pending debounce timeouts when component unmounts
      Object.values(debounceRef.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
      debounceRef.current = {};
    };
  }, []);

  const fetchReasons = async () => {
    try {
      // Only fetch undelivered reasons since lost/damaged no longer need reason dropdowns
      const undeliveredResponse = await API.Api(DRS_UNDELIVERED_REASONS).get();

      if (undeliveredResponse?.response?.data?.data) {
        const transformedUndeliveredReasons =
          undeliveredResponse.response.data.data.map((reason) => ({
            value: reason.reason_code,
            label: reason.description,
            _id: reason._id,
          }));
        setUndeliveredReasons(transformedUndeliveredReasons);
      }
    } catch (error) {
      message.error("Failed to fetch reasons");
      console.error("Error fetching reasons:", error);
    }
  };

  // Common function to fetch shipment data with filters
  const fetchShipmentData = async (
    page = 1,
    pageSize = 100,
    filters = null
  ) => {
    if (!isCentralOperator) {
      message.error(
        "Access denied. Only Central Operators can view this section."
      );
      return;
    }

    if (
      !dateRange[0]?.startOf("day").toISOString() ||
      !dateRange[1]?.endOf("day").toISOString()
    ) {
      return;
    }

    try {
      const queryParams = {
        startTime: dateRange[0].startOf("day").toISOString(),
        endTime: dateRange[1].endOf("day").toISOString(),
        pageNumber: page,
        pageSize: pageSize,
        customerPhoneMasked: false,
      };

      // Use provided filters or fall back to current columnFilters state
      const filtersToUse = filters || columnFilters;

      // Add column filters to query params - pass the column key with its value
      Object.entries(filtersToUse).forEach(([columnKey, columnValue]) => {
        if (columnValue && columnValue.trim() !== "") {
          queryParams[columnKey] = columnValue.trim();
        }
      });

      const queryString = new URLSearchParams(queryParams).toString();

      const response = await API.Api(
        GET_PRS_DATA_V2 + "?" + queryString
      ).getCustomHeader({
        kid: "1",
        authtype: "jwt",
      });

      if (response?.response?.data?.status === 200) {
        // Always clear existing data and set new data to prevent showing stale data
        const newData = response.response.data.data || [];
        const totalCount = response.response.data.totalCount || 0;

        // Extract searchable fields from API response
        const apiSearchableFields =
          response.response.data.queryParams?.searchable || [];
        setSearchableFields(apiSearchableFields);

        // Clear data first to prevent stale data issues
        setData([]);

        // Use setTimeout to ensure state update is processed
        setTimeout(() => {
          setData(newData);
          setPagination({
            current: page,
            pageSize,
            total: totalCount,
          });
        }, 0);

        // Initialize column filters based on the new data structure and searchable fields
        if (newData.length > 0 && Object.keys(columnFilters).length <= 1) {
          const newFilters = initializeColumnFilters(
            newData,
            apiSearchableFields
          );
          // Preserve existing filters if they exist
          Object.keys(columnFilters).forEach((key) => {
            if (columnFilters[key]) {
              newFilters[key] = columnFilters[key];
            }
          });
          setColumnFilters(newFilters);
        }
      } else {
        // Clear data on error to prevent showing stale data
        setData([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        message.error(
          response?.response?.data?.message || "Failed to fetch shipments"
        );
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Error fetching shipments"
      );
    }
  };

  // Function to handle all API calls
  const fetchAllData = async (
    page = 1,
    pageSize = 100,
    filters = null,
    skipDateCheck = false
  ) => {
    // Prevent multiple simultaneous calls
    if (apiCallInProgress.current) {
      return;
    }

    if (!skipDateCheck && (!dateRange[0] || !dateRange[1])) {
      return;
    }

    // Use current filters if none provided
    const filtersToUse = filters || columnFilters;
    const currentPage = page || pagination.current || 1;
    const currentPageSize = pageSize || pagination.pageSize || 100;

    apiCallInProgress.current = true;
    setLoading(true);

    try {
      setLoading(true);
      // Clear data at the start of fetch to prevent stale data
      setData([]);

      await fetchShipmentData(currentPage, currentPageSize, filtersToUse);
    } catch (error) {
      // Errors are handled by individual API functions
    } finally {
      apiCallInProgress.current = false;
      setLoading(false);
      setIsInitialLoading(false);
    }
  };

  // Get current location
  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          let errorMessage = "Unable to retrieve your location";
          if (error.code === 1) {
            errorMessage =
              "Location permission denied. Please enable location access.";
          }
          reject(new Error(errorMessage));
        }
      );
    });
  };

  // Handle status update
  const updateShipmentStatus = async (records, formValues) => {
    try {
      const status = formValues.status;
      const remarks = formValues.remarks || "";

      // Get user info for user_id
      const userinfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
      const userId = userinfo?.userId || userinfo?.id;
      const userInfo = getUserAndPremiseId();
      const userName = `${userInfo?.firstName || ""} ${userInfo?.lastName || ""}`.trim();

      // Handle both single record and bulk update
      const awbNumbers = Array.isArray(records)
        ? records.map((record) => record.awbNumber)
        : [records.awbNumber];

      // Get location (required for new API)
      let location;
      try {
        location = await getLocation();
      } catch (error) {
        message.error(error.message);
        return false;
      }

      // Construct payload for new API
      const payload = {
        user_id: userId,
        userName: userName, // Added
        status: status,
        source: "NBA_WEB",
        event_timestamp: new Date().toISOString(),
        location: location,
        details: {
          remarks: remarks,
          shipment_groups: [
            {
              awb_numbers: awbNumbers,
            },
          ],
        },
      };

      // Add reason_code to details if it's undelivered status
      if (status === "undelivered") {
        payload.details.reason_code =
          formValues.reason?.value || formValues.reason;
      }

      // For lost, damaged, undelivered use the new exceptions API
      if (
        status === "lost" ||
        status === "damaged" ||
        status === "undelivered" ||
        status === "rto_undelivered"
      ) {
        const response = await API.Api(DRS_ORDER_EXCEPTIONS).post(payload);

        if (
          !response.status ||
          (response.response?.status !== 200 &&
            response.response?.status !== 201)
        ) {
          throw new Error(
            response.response?.data?.message ||
            "Failed to update status via exceptions API"
          );
        }
      }

      const recordCount = awbNumbers.length;
      message.success(
        `Status updated successfully for ${recordCount} record${recordCount > 1 ? "s" : ""
        }`
      );
      return true;
    } catch (error) {
      message.error(error?.message || "Failed to update status");
      return false;
    }
  };

  // Handle form submit
  const handleFormSubmit = async (values) => {
    try {
      setSubmitLoading(true);
      const recordsToUpdate = isBulkUpdate ? selectedRows : currentRecord;

      // Handle delivered status - navigate to POD upload page
      if (values.status === "delivered" || values.status === "rto_delivered") {
        const selectedShipments = Array.isArray(recordsToUpdate)
          ? recordsToUpdate
          : [recordsToUpdate];

        // Navigate to POD upload page with form values and selected shipments
        navigate("/delivered-pod-upload", {
          state: {
            selectedShipments,
            status: values.status,
            remarks: values.remarks,
            isBulkUpdate,
          },
        });

        // Close modal and reset form
        setIsUpdateModalOpen(false);
        updateForm.resetFields();
        setSelectedStatus("");
        setSelectedReason(null);
        setIsBulkUpdate(false);
        return;
      }

      // Update status for other statuses (no POD upload required)
      const success = await updateShipmentStatus(recordsToUpdate, values);

      if (success) {
        setIsUpdateModalOpen(false);
        updateForm.resetFields();
        setSelectedStatus("");
        setSelectedReason(null);
        setIsBulkUpdate(false);
        // Refresh data
        fetchAllData();
        // Clear selections
        handleClearSelection();
      }
    } catch (error) {
      message.error(error?.message || "Failed to process your request");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Open update modal
  const openUpdateModal = (record) => {
    setIsBulkUpdate(false);
    setCurrentRecord(record);
    updateForm.setFieldsValue({
      status: "",
      remarks: "",
    });
    setSelectedStatus("");
    setSelectedReason(null);
    setIsUpdateModalOpen(true);
  };

  // Handle bulk update
  const handleBulkUpdate = () => {
    if (selectedRows.length === 0) {
      message.warning("Please select at least one record to update");
      return;
    }

    setIsBulkUpdate(true);
    setCurrentRecord(null);
    updateForm.setFieldsValue({
      status: "",
      remarks: "",
    });
    setSelectedStatus("");
    setSelectedReason(null);
    setIsUpdateModalOpen(true);
  };

  // Selection handlers
  const handleClearSelection = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  };

  const handleTableChange = (newPagination) => {
    if (newPagination.current) {
      const updatedPagination = {
        current: newPagination.current,
        pageSize: newPagination.pageSize || 100,
        total: pagination.total,
      };

      setPagination(updatedPagination);

      fetchAllData(newPagination.current, newPagination.pageSize || 100);
    }
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      const newDateRange = [dates[0], dates[1]];

      setDateRange(newDateRange);

      // Reset pagination to first page when date changes
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));
    }
  };

  // Update filter state without triggering API call
  const handleColumnFilter = (value, field) => {
    // Create updated filters object immediately
    const updatedFilters = {
      ...columnFilters,
      [field]: value,
    };

    // Update the filter state immediately for UI responsiveness
    setColumnFilters(updatedFilters);
  };

  // Trigger search with current filters
  const handleSearch = (field = null, value = null) => {
    // If specific field and value provided, update that filter first
    let filtersToUse = columnFilters;
    if (field && value !== null) {
      filtersToUse = {
        ...columnFilters,
        [field]: value,
      };
      setColumnFilters(filtersToUse);
    }

    // Reset pagination to first page when searching
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));

    // Clear existing timeouts
    Object.values(debounceRef.current).forEach((timeout) => {
      if (timeout) clearTimeout(timeout);
    });
    debounceRef.current = {};

    // Call API with current filters
    fetchAllData(1, pagination.pageSize, filtersToUse);
  };

  const handleClearFilters = () => {
    // Clear all existing debounce timeouts
    Object.values(debounceRef.current).forEach((timeout) => {
      if (timeout) clearTimeout(timeout);
    });
    debounceRef.current = {};

    // Clear all column filters - reset based on current data structure and searchable fields
    const clearedFilters =
      data.length > 0
        ? initializeColumnFilters(data, searchableFields)
        : getInitialState().columnFilters;

    setColumnFilters(clearedFilters);

    // Reset pagination to first page
    setPagination({
      ...pagination,
      current: 1,
    });

    // Fetch data immediately without debounce (filters will be empty after reset)
    fetchAllData(1, pagination.pageSize, clearedFilters);
  };

  const onSearch = async (e) => {
    const searchQuery = e.target.value;

    // Update AWB number filter and trigger search
    handleColumnFilter(searchQuery, "search");

    // Clear existing debounce timeout
    if (debounceRef.current.search) {
      clearTimeout(debounceRef.current.search);
    }

    // Debounce the search
    debounceRef.current.search = setTimeout(() => {
      handleSearch("search", searchQuery);
    }, 500);
  };

  // Function to convert camelCase/snake_case to Title Case
  const generateTitle = (key) => {
    return key
      .replace(/([A-Z])/g, " $1") // Add space before capital letters
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/\b\w/g, (l) => l.toUpperCase()) // Capitalize first letter of each word
      .trim();
  };

  // Function to get column width based on field type
  const getColumnWidth = (key, title) => {
    // Date fields
    if (
      key.toLowerCase().includes("at") ||
      key.toLowerCase().includes("date")
    ) {
      return 160;
    }
    // Phone/Mobile fields
    if (
      key.toLowerCase().includes("phone") ||
      key.toLowerCase().includes("mobile")
    ) {
      return 150;
    }
    // Address fields
    if (key.toLowerCase().includes("address")) {
      return 200;
    }
    // ID fields
    if (key.toLowerCase().includes("id")) {
      return 130;
    }
    // Name fields
    if (key.toLowerCase().includes("name")) {
      return 150;
    }
    // Status fields
    if (
      key.toLowerCase().includes("status") ||
      key.toLowerCase().includes("number")
    ) {
      return 160;
    }
    // Default width
    return 120;
  };

  // Function to check if a value is a valid date string
  const isValidDateString = (value) => {
    if (!value || typeof value !== "string") return false;

    // Check if it's a valid ISO date string or other common date formats
    const date = moment(value);
    return (
      date.isValid() &&
      // Check for ISO date format (with T and Z)
      (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value) ||
        // Check for other common date formats
        /^\d{4}-\d{2}-\d{2}/.test(value))
    );
  };

  // Function to get custom render function for specific fields
  const getCustomRender = (key) => {
    // Universal renderer - checks actual value for dates
    const universalRenderer = (text) => {
      if (!text) return "-";

      // Check if it's actually a valid date string
      if (isValidDateString(text)) {
        return moment(text).format("DD-MM-YYYY hh:mm A");
      }

      // If not a date, return as is
      return text;
    };

    // Apply universal rendering for fields that might contain dates
    if (
      key.toLowerCase().includes("at") ||
      key.toLowerCase().includes("date")
    ) {
      return universalRenderer;
    }

    // Status fields with underscores - handle different status types
    if (key.toLowerCase().includes("status")) {
      const StatusRenderer = (status) => {
        if (!status || status === "NA") return "-";

        let color = "";
        let displayText = "";

        switch (status?.toLowerCase()) {
          case "out_for_delivery":
          case "out for delivery":
            color = "#2E77D0";
            displayText = "Out for Delivery";
            break;
          case "undelivered":
            color = "#FF0000";
            displayText = "Undelivered";
            break;
          case "lost":
            color = "#FF0000";
            displayText = "Lost";
            break;
          case "damaged":
            color = "#FFA500";
            displayText = "Damaged";
            break;
          case "delivered":
            color = "#008000";
            displayText = "Delivered";
            break;
          case "rto_delivered":
            color = "#008000";
            displayText = "RTO Delivered";
            break;
          case "rto_undelivered":
            color = "#FF0000";
            displayText = "RTO Undelivered";
            break;
          default:
            color = "#808080";
            displayText = status?.replace(/_/g, " ") || "N/A";
        }

        return (
          <div
            style={{
              color: color,
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span style={{ marginRight: "5px" }}>●</span>
            <span>{displayText}</span>
          </div>
        );
      };
      StatusRenderer.displayName = "StatusRenderer";
      return StatusRenderer;
    }

    return undefined;
  };

  // Helper function to create column filter
  const getColumnSearchProps = (dataIndex, placeholder) => {
    // Define minimum character requirements for different fields
    const getMinChars = (fieldName) => {
      if (
        fieldName === "vendorCode" ||
        fieldName === "pickupPincode" ||
        fieldName === "dpPincode"
      ) {
        return 2;
      }
      return 3;
    };

    const minChars = getMinChars(dataIndex);
    const placeholderText = `Search ${placeholder}...`;

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={placeholderText}
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleColumnFilter(value, dataIndex);
            }}
            onPressEnter={() => {
              const value = selectedKeys[0] || "";
              handleSearch(dataIndex, value);
              confirm();
            }}
            style={{
              marginBottom: 8,
              display: "block",
              borderRadius: "6px",
            }}
            allowClear={false}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                const value = selectedKeys[0] || "";
                handleSearch(dataIndex, value);
                confirm();
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
                handleSearch(dataIndex, "");
                confirm(); // Close the filter popup
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <div className={`filter-icon-container ${filtered ? "filtered" : ""}`}>
          <FilterOutlined
            className={`filter-icon ${filtered ? "filtered" : ""}`}
          />
        </div>
      ),
      filteredValue: columnFilters[dataIndex]
        ? [columnFilters[dataIndex]]
        : null,
    };
  };

  // Dynamic column generation based on data and searchable fields
  const generateColumns = (dataSource, searchableFieldsArray = []) => {
    if (!dataSource || dataSource.length === 0) {
      return [];
    }

    const sampleRecord = dataSource[0];
    const keys = Object.keys(sampleRecord);

    const dynamicColumns = keys.map((key) => {
      const title = generateTitle(key);
      const width = getColumnWidth(key, title);
      const customRender = getCustomRender(key);

      const baseColumn = {
        title,
        dataIndex: key,
        key,
        ...(width && { width }),
        ...(customRender && { render: customRender }),
      };

      // Add search functionality dynamically based on searchable fields from API
      const isSearchableField = searchableFieldsArray.includes(key);
      if (isSearchableField) {
        return {
          ...baseColumn,
          ...getColumnSearchProps(key, title),
        };
      }

      // Add ellipsis for address and long text fields
      if (
        key.toLowerCase().includes("address") ||
        key.toLowerCase().includes("name")
      ) {
        baseColumn.ellipsis = true;
      }

      return baseColumn;
    });

    // Add action column at the end
    const actionColumn = {
      title: "Action",
      key: "action",
      width: 150,
      render: (_, record) => {
        const isSelected = selectedRowKeys.includes(
          record.awbNumber || record.id
        );

        return (
          <Space size="middle">
            {!isSelected && (
              <Button
                type="primary"
                onClick={() => openUpdateModal(record)}
                size="small"
              >
                UPDATE STATUS
              </Button>
            )}
          </Space>
        );
      },
    };

    return [actionColumn, ...dynamicColumns];
  };

  // Get dynamic columns with searchable fields
  const columns = generateColumns(data, searchableFields);

  const activeFiltersCount = Object.values(columnFilters).filter(
    (value) => value && value.trim() !== ""
  ).length;

  // Fallback static columns for when no data is available
  const fallbackColumns = [
    {
      title: "AWB Number",
      dataIndex: "awbNumber",
      key: "awbNumber",
      render: (text) => (
        <span style={{ fontWeight: "500", color: "#1890ff" }}>{text}</span>
      ),
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      render: (text) => text || "N/A",
    },
    {
      title: (
        <>
          Customer
          <br />
          Mobile No.
        </>
      ),
      dataIndex: "customerPhone",
      key: "customerPhone",
      render: (text) => text || "N/A",
    },
    {
      title: "Delivery Address",
      dataIndex: "address",
      key: "address",
      render: (text) => (
        <Tooltip title={text}>
          <span>
            {text?.length > 50 ? `${text.substring(0, 50)}...` : text || "N/A"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Shipment Type",
      dataIndex: "shipmentType",
      key: "shipmentType",
      render: (text) => text || "N/A",
    },
    {
      title: "Current Status",
      dataIndex: "currentStatus",
      key: "currentStatus",
      render: (status) => {
        let color = "";
        let displayText = "";

        switch (status?.toLowerCase()) {
          case "out_for_delivery":
          case "out for delivery":
            color = "#2E77D0";
            displayText = "Out for Delivery";
            break;
          case "undelivered":
            color = "#FF0000";
            displayText = "Undelivered";
            break;
          case "lost":
            color = "#FF0000";
            displayText = "Lost";
            break;
          case "damaged":
            color = "#FFA500";
            displayText = "Damaged";
            break;
          default:
            color = "#808080";
            displayText = status?.replace(/_/g, " ") || "N/A";
        }

        return (
          <div
            style={{
              color: color,
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span style={{ marginRight: "5px" }}>●</span>
            <span>{displayText}</span>
          </div>
        );
      },
    },
    {
      title: (
        <>
          Created
          <br />
          Date & Time
        </>
      ),
      dataIndex: "createdDate",
      key: "createdDate",
      render: (text) =>
        text ? moment(text).format("DD/MM/YYYY hh:mm A") : "N/A",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        const isSelected = selectedRowKeys.includes(record.awbNumber);

        return (
          <Space size="middle">
            {!isSelected && (
              <Button
                type="primary"
                onClick={() => openUpdateModal(record)}
                size="small"
              >
                UPDATE STATUS
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys, selectedRecords) => {
      setSelectedRowKeys(selectedKeys);
      setSelectedRows(selectedRecords);
    },
  };

  const itemRender = (_, type, originalElement) => {
    if (type === "prev") {
      return <a>Previous</a>;
    }
    if (type === "next") {
      return <a>Next</a>;
    }
    return originalElement;
  };

  // Show access denied if not Central Operator
  if (!isCentralOperator) {
    return (
      <div className="status-updates-access-denied">
        <Card>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <ExclamationCircleOutlined
              style={{
                fontSize: "48px",
                color: "#faad14",
                marginBottom: "16px",
              }}
            />
            <h2>Access Denied</h2>
            <p>Only Central Operators are authorized to access this section.</p>
            <p>Current Role: {userRole || "Unknown"}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="rto-requests-list master-main-container m-1 p-1">
      {/* Date Range and Action Buttons Row */}
      <Row gutter={[8, 24]} align="bottom" className="mb-4">
        <Col xs={24} sm={12} md={14} lg={5}>
          <Form.Item
            label={
              <Space size="small">
                <span>Order Created Date Range</span>
              </Space>
            }
            required
            tooltip={{
              title:
                "This date range filters orders by their creation date. All shipments will be shown for orders created within this period.",
              placement: "topLeft",
            }}
            style={{ marginBottom: 0 }}
          >
            <RangePicker
              className="input-field-style date-range-picker"
              value={dateRange}
              format={dateFormat_dmy}
              onChange={handleDateRangeChange}
              allowClear={false}
              disabledDate={(current) =>
                current && current > moment().endOf("day")
              }
              placeholder={["Start Date", "End Date"]}
              separator="→"
            />
          </Form.Item>
        </Col>
        <Col>
          <div className="action-buttons-container">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => {
                const currentPage = pagination.current || 1;
                const currentPageSize = pagination.pageSize || 100;
                fetchAllData(currentPage, currentPageSize, columnFilters);
              }}
            >
              Refresh Data
            </Button>
          </div>
        </Col>
      </Row>

      <div className="p-1 rto-actions-header">
        <div className="bulk-actions">
          {selectedRowKeys.length > 0 ? (
            <div className="bulk-action-buttons">
              <span className="selected-count">
                {selectedRowKeys.length} item
                {selectedRowKeys.length > 1 ? "s" : ""} selected
              </span>
              <Button
                type="default"
                onClick={handleClearSelection}
                style={{ marginRight: "8px" }}
              >
                Clear Selection
              </Button>
              <Button
                type="primary"
                onClick={handleBulkUpdate}
                disabled={loading}
                className="bulk-update-btn"
              >
                Bulk Update Status ({selectedRowKeys.length})
              </Button>
            </div>
          ) : (
            <div className="bulk-info-message">
              <span className="info-text">
                💡 Select shipments using checkboxes to enable bulk status
                updates
              </span>
            </div>
          )}
        </div>
        <div className="search-and-filter-section">
          <div className="search-section">
            <Input
              placeholder="Search by AWB Number or Customer Name"
              bordered
              style={{ borderRadius: "5px", height: "40px", width: "365px" }}
              suffix={<img src={search_icon} alt="search" />}
              onChange={onSearch}
            />
          </div>
          <div className="filter-actions">
            <Button
              type="default"
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
              disabled={activeFiltersCount === 0}
            >
              Clear Filters{" "}
              {activeFiltersCount > 0 && `(${activeFiltersCount})`}
            </Button>
          </div>
        </div>
      </div>

      <Table
        columns={data.length > 0 ? columns : fallbackColumns}
        dataSource={data}
        loading={loading}
        rowKey={(record, index) =>
          record.awbNumber || record.cAwbNumber || record.id || `row-${index}`
        }
        rowSelection={rowSelection}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${pagination.total} items`,
          showQuickJumper: true,
          pageSizeOptions: ["50", "100", "200", "500"],
          position: ["bottomRight"],
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
      />

      {/* Status Update Modal */}
      <Modal
        title={
          <div style={{ fontSize: "18px", fontWeight: "bold" }}>
            {isBulkUpdate
              ? `Bulk Update Status (${selectedRows.length} records)`
              : "Update Shipment Status"}
          </div>
        }
        open={isUpdateModalOpen}
        onCancel={() => {
          setIsUpdateModalOpen(false);
          updateForm.resetFields();
          setSelectedStatus("");
          setSelectedReason(null);
          setIsBulkUpdate(false);
        }}
        footer={null}
        width={600}
        bodyStyle={{ padding: "24px" }}
        maskClosable={false}
      >
        <Form
          form={updateForm}
          layout="vertical"
          onFinish={handleFormSubmit}
          requiredMark="optional"
        >
          <Form.Item
            name="status"
            label={<span style={{ fontWeight: "bold" }}>New Status</span>}
            rules={[{ required: true, message: "Please select a status" }]}
          >
            <Select
              placeholder="Select status"
              size="large"
              onChange={(value) => {
                setSelectedStatus(value);
                setSelectedReason(null);
              }}
            >
              <Select.Option value="delivered">Delivered</Select.Option>
              <Select.Option value="undelivered">Undelivered</Select.Option>
              <Select.Option value="rto_delivered">RTO Delivered</Select.Option>
              <Select.Option value="rto_undelivered">
                RTO Undelivered
              </Select.Option>
              <Select.Option value="lost">Lost</Select.Option>
              <Select.Option value="damaged">Damaged</Select.Option>
            </Select>
          </Form.Item>

          {(selectedStatus === "delivered" ||
            selectedStatus === "rto_delivered") && (
              <>
                <Form.Item
                  name="remarks"
                  label={<span style={{ fontWeight: "bold" }}>Remarks</span>}
                >
                  <Input.TextArea
                    placeholder="Enter delivery remarks"
                    autoSize={{ minRows: 3, maxRows: 5 }}
                  />
                </Form.Item>
              </>
            )}

          {selectedStatus === "undelivered" && (
            <>
              <Form.Item
                name="reason"
                label={<span style={{ fontWeight: "bold" }}>Reason</span>}
                rules={[{ required: true, message: "Please select a reason" }]}
              >
                <Select
                  placeholder="Select reason"
                  size="large"
                  onChange={(value, option) => setSelectedReason(option)}
                  labelInValue
                >
                  {undeliveredReasons.map((reason, index) => (
                    <Select.Option
                      key={index}
                      value={reason.value || reason.reason_code}
                      label={reason.label || reason.description}
                      _id={reason._id}
                    >
                      {reason.label || reason.description}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="remarks"
                label={<span style={{ fontWeight: "bold" }}>Remarks</span>}
              >
                <Input.TextArea
                  placeholder="Enter remarks"
                  autoSize={{ minRows: 3, maxRows: 5 }}
                />
              </Form.Item>
            </>
          )}

          {(selectedStatus === "lost" ||
            selectedStatus === "damaged" ||
            selectedStatus === "rto_delivered" ||
            selectedStatus === "rto_undelivered") && (
              <>
                <Form.Item
                  name="remarks"
                  label={<span style={{ fontWeight: "bold" }}>Remarks</span>}
                >
                  <Input.TextArea
                    placeholder="Enter remarks"
                    autoSize={{ minRows: 3, maxRows: 5 }}
                  />
                </Form.Item>
              </>
            )}

          <Form.Item style={{ marginTop: "24px", textAlign: "right" }}>
            <Button
              onClick={() => {
                setIsUpdateModalOpen(false);
                updateForm.resetFields();
                setSelectedStatus("");
                setSelectedReason(null);
                setIsBulkUpdate(false);
              }}
              style={{ marginRight: 12 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitLoading}>
              {selectedStatus === "delivered" ||
                selectedStatus === "rto_delivered"
                ? isBulkUpdate
                  ? `Next → POD Upload (${selectedRows.length} Records)`
                  : "Next → Go to POD Upload"
                : isBulkUpdate
                  ? `Update ${selectedRows.length} Records`
                  : "Update Status"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StatusUpdates;
